import PageRecord from "models/view-models/page-record";
import QueryHookFactory from "utilities/hooks/queries/query-hook-factory";
import ServiceFactory from "utilities/services/service-factory";
import ServiceHookFactory from "utilities/services/service-hook-factory";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

export interface PageBasePathParams {
    notebookId: number;
}

export interface PageResourcePathParams {
    id: number;
}

export interface PageQueryParams {}

export interface SharedWithMeQueryParams {}

export interface PageMovePathParams {}

export interface PageRelocatePathParams {
    id: number;
}

export interface PageMoveBodyParams {
    movingPageId: number;
    precedingPageId?: number;
}

export interface PageRelocateBodyParams {
    destinationNotebookId: number;
}

export interface UpdatePageAccessDetailsBodyParams {
    isPubliclyAccessible: boolean;
    isCloningPermitted: boolean;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const baseEndpoint = "pages";
const sharedWithMe = `${baseEndpoint}/sharedwithme`;
const moveEndpoint = `${baseEndpoint}/move`;
const resourceEndpoint = `${baseEndpoint}/:id`;
const updateAccessDetailsEndpoint = `${resourceEndpoint}/accessdetails`;
const relocateEndpoint = `${baseEndpoint}/:id/relocate`;
const resourceType = PageRecord;

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Service
// -------------------------------------------------------------------------------------------------

const GET_PAGE_QUERY_KEY = "getPage";

const PageService = {
    getPageKey: GET_PAGE_QUERY_KEY,
    create: ServiceFactory.nestedCreate<PageRecord, PageBasePathParams>(
        resourceType,
        baseEndpoint
    ),

    get: ServiceFactory.get<PageRecord, PageResourcePathParams>(
        resourceType,
        resourceEndpoint
    ),

    move: ServiceFactory.patch<
        Array<number>,
        PageMovePathParams,
        PageMoveBodyParams
    >(Array, moveEndpoint),

    useCreate: ServiceHookFactory.useNestedCreate<
        PageRecord,
        PageBasePathParams
    >(resourceType, baseEndpoint),

    useGet: ServiceHookFactory.useGet<PageRecord, PageResourcePathParams>(
        resourceType,
        resourceEndpoint
    ),

    useGetQuery: QueryHookFactory.useGetQuery<
        PageRecord,
        PageResourcePathParams
    >(GET_PAGE_QUERY_KEY, resourceType, resourceEndpoint),

    useMove: ServiceHookFactory.usePatch<
        Array<number>,
        PageMovePathParams,
        PageMoveBodyParams
    >(Array, moveEndpoint),

    useRelocate: ServiceHookFactory.usePatch<
        PageRecord,
        PageRelocatePathParams,
        PageRelocateBodyParams
    >(resourceType, relocateEndpoint),

    useUpdate: ServiceHookFactory.useUpdate<PageRecord, PageResourcePathParams>(
        resourceType,
        resourceEndpoint
    ),

    useUpdateAccessDetails: ServiceHookFactory.useUpdateFromPutParams<
        PageRecord,
        UpdatePageAccessDetailsBodyParams,
        PageResourcePathParams
    >(resourceType, updateAccessDetailsEndpoint),

    useDelete: ServiceHookFactory.useDelete(resourceEndpoint),

    useSharedWithMe: ServiceHookFactory.useList<
        PageRecord,
        SharedWithMeQueryParams
    >(resourceType, sharedWithMe),
};

// #endregion Service

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export default PageService;

// #endregion Exports
